import { Alert, App, Button, Card, Descriptions, Input, Modal, Space, Spin, Tag } from 'antd'
import { EditOutlined, CloseOutlined, DatabaseOutlined, ToolOutlined, DeleteOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useTenants } from '../hooks/useTenants'
import { formatCountryCodes } from '../types/tenant.types'
import { type TenantLicense, setupUpdate, setupSeeds, destroyTenant } from '../services/tenant-api'
import { FeatureFlagTag } from './FeatureFlagTag'
import { EditTenantModal } from './EditTenantModal'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin, isWorkspaceAdmin } from '../../../utils/roles'
import { TenantPermissionManagers } from './TenantPermissionManagers'

const licenseLabels: Record<keyof TenantLicense, string> = {
  EmpCount: 'People',
  EmpPsgCount: 'Payroll',
  EmpLveCount: 'Leave',
  EmpClaCount: 'Claim',
  EmpAttCount: 'Attendance',
  EmpPfmCount: 'Performance'
}

interface TenantDetailDrawerProps {
  tenantCode: string | null
  onClose: () => void
}

export function TenantDetailDrawer({ tenantCode, onClose }: TenantDetailDrawerProps) {
  const { user } = useAuth()
  const { message, modal } = App.useApp()
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [setupLoading, setSetupLoading] = useState(false)
  const [destroyLoading, setDestroyLoading] = useState(false)
  const [destroyModalOpen, setDestroyModalOpen] = useState(false)
  const [passCode, setPassCode] = useState('')
  const { tenants, loading, error, refresh } = useTenants({
    includeInactive: true,
    includeFeatureFlags: true,
    includeUserCount: true
  })

  const canEditWorkspace = isSysAdmin(user) // || isWorkspaceAdmin(user)
  const canSetup = isSysAdmin(user) || isWorkspaceAdmin(user)

  const tenant = tenantCode ? tenants.find(t => t.tenantCode === tenantCode) : null

  const handleEditSuccess = () => {
    refresh()
  }

  const handleSetupUpdate = () => {
    if (!tenant) return
    modal.confirm({
      title: 'Check table schema',
      content:
        'Checks and repairs database table schemas to ensure they match the expected structure. Use this tool if a system update fails to apply changes correctly or if database inconsistencies are detected.',
      okText: 'Proceed',
      onOk: async () => {
        try {
          setSetupLoading(true)
          await setupUpdate(tenant.tenantCode)
          message.success('Table schema check triggered successfully')
        } catch {
          message.error('Failed to trigger table schema check')
        } finally {
          setSetupLoading(false)
        }
      }
    })
  }

  const handleSetupSeeds = () => {
    if (!tenant) return
    modal.confirm({
      title: 'Check system data',
      content:
        'Inserts or updates essential system data required for proper application functionality. Use this option to manually populate default configurations, lookup values, or reference data after an update or during troubleshooting.',
      okText: 'Proceed',
      onOk: async () => {
        try {
          setSetupLoading(true)
          await setupSeeds(tenant.tenantCode)
          message.success('System data check triggered successfully')
        } catch {
          message.error('Failed to trigger system data check')
        } finally {
          setSetupLoading(false)
        }
      }
    })
  }

  const canDestroy =
    isSysAdmin(user) &&
    tenant?.destroyDate &&
    new Date(tenant.destroyDate).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)

  const handleDestroy = async () => {
    if (!tenant || !passCode) return
    try {
      setDestroyLoading(true)
      await destroyTenant(tenant.tenantCode, passCode)
      message.success('Workspace destroyed successfully')
      setDestroyModalOpen(false)
      setPassCode('')
      onClose()
    } catch {
      message.error('Failed to destroy workspace. Check your pass code.')
    } finally {
      setDestroyLoading(false)
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexShrink: 0
        }}
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Workspace details</h2>
        <Space>
          {tenant && canEditWorkspace && (
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditModalOpen(true)}>
              Edit
            </Button>
          )}
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
        </Space>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && !tenant ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert message="Error loading workspace" description={error.message} type="error" showIcon />
        ) : !tenant ? (
          <Alert
            message="Workspace not found"
            description={`No workspace found with code: ${tenantCode}`}
            type="warning"
            showIcon
          />
        ) : (
          <>
            <Card title={tenant.tenantName} style={{ marginBottom: 16 }} size="small">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Workspace code">
                  <Tag color="default">{tenant.workspaceCode}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Tenant code">
                  <Tag color="geekblue">{tenant.tenantCode}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Countries">{formatCountryCodes(tenant.countryCodes)}</Descriptions.Item>
                <Descriptions.Item label="Time zone">{tenant.timeZoneId}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  {(() => {
                    const shortLabels: Record<string, string> = { new: 'New', demo: 'Demo', trial: 'Trial', uat: 'UAT' }
                    const colors: Record<string, string> = {
                      new: 'blue',
                      demo: 'orange',
                      trial: 'purple',
                      uat: 'magenta'
                    }
                    const type = tenant.workspaceType
                    const label = (type && shortLabels[type]) || type || '-'
                    const color = (type && colors[type]) || 'default'
                    return <Tag color={color}>{label}</Tag>
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {(() => {
                    if (!tenant.inactiveDate) {
                      return <Tag color="green">Active</Tag>
                    }
                    const inactiveDate = new Date(tenant.inactiveDate)
                    const formatted = inactiveDate.toLocaleDateString()
                    const isFuture = inactiveDate.getTime() > Date.now()
                    return isFuture ? (
                      <Tag color="gold">Active until {formatted}</Tag>
                    ) : (
                      <Tag color="red">Inactive since {formatted}</Tag>
                    )
                  })()}
                </Descriptions.Item>
                {tenant.destroyDate && (
                  <Descriptions.Item label="Destroy date">
                    <Tag
                      color={
                        new Date(tenant.destroyDate).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
                          ? 'red'
                          : 'orange'
                      }
                    >
                      {new Date(tenant.destroyDate).toLocaleDateString()}
                    </Tag>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created date">
                  {new Date(tenant.createdDate).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Created by">{tenant.createdBy}</Descriptions.Item>
                {tenant.userCount !== undefined && (
                  <Descriptions.Item label="Total users">{tenant.userCount}</Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card title="Feature flags" size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {tenant.featureFlags.filter(ff => ff.disabledDate === null).length > 0 ? (
                  tenant.featureFlags
                    .filter(ff => ff.disabledDate === null)
                    .map(ff => <FeatureFlagTag key={ff.featureFlag} flag={ff.featureFlag} />)
                ) : (
                  <span style={{ color: '#999' }}>No feature flags enabled</span>
                )}
              </div>
            </Card>

            <Card title="License" size="small" style={{ marginBottom: 16 }}>
              {(() => {
                const license: TenantLicense = tenant.licenseJsonb ? JSON.parse(tenant.licenseJsonb) : {}
                const hasLicense = Object.values(license).some(v => v !== undefined && v !== null)

                if (!hasLicense) {
                  return <span style={{ color: '#999' }}>No license limits configured</span>
                }

                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {(Object.keys(licenseLabels) as (keyof TenantLicense)[]).map(key => {
                      const value = license[key]
                      if (value === undefined || value === null) return null
                      return (
                        <div
                          key={key}
                          style={{ textAlign: 'center', padding: '8px', background: '#fafafa', borderRadius: '4px' }}
                        >
                          <div style={{ fontSize: '12px', color: '#666' }}>{licenseLabels[key]}</div>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{value}</div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </Card>

            {canSetup && <TenantPermissionManagers tenantCode={tenant.tenantCode} />}

            {canSetup && (
              <Card title="Setup actions" size="small" style={{ marginTop: 16 }}>
                <Space>
                  <Button icon={<DatabaseOutlined />} onClick={handleSetupUpdate} loading={setupLoading}>
                    Check table schema
                  </Button>
                  <Button icon={<ToolOutlined />} onClick={handleSetupSeeds} loading={setupLoading}>
                    Check system data
                  </Button>
                  {canDestroy && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setPassCode('')
                        setDestroyModalOpen(true)
                      }}
                    >
                      Destroy
                    </Button>
                  )}
                </Space>
              </Card>
            )}

            {canEditWorkspace && (
              <EditTenantModal
                open={editModalOpen}
                tenant={tenant}
                onClose={() => setEditModalOpen(false)}
                onSuccess={handleEditSuccess}
              />
            )}

            <Modal
              title="Destroy workspace"
              open={destroyModalOpen}
              onOk={handleDestroy}
              onCancel={() => {
                setDestroyModalOpen(false)
                setPassCode('')
              }}
              okText="Destroy"
              okButtonProps={{ danger: true, disabled: !passCode }}
              confirmLoading={destroyLoading}
            >
              <p>
                This will permanently destroy <strong>{tenant?.tenantName}</strong> ({tenant?.tenantCode}). This action
                cannot be undone.
              </p>
              <Input.Password
                placeholder="Enter pass code"
                value={passCode}
                onChange={e => setPassCode(e.target.value)}
              />
            </Modal>
          </>
        )}
      </div>
    </div>
  )
}
